import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';

export default function MiamiServices() {
  const pageData = {
    title: "Impact Window Services in Miami: Areas Served & Project Types",
    metaDescription: "Impact window and door installation across Miami-Dade, Broward, and Palm Beach counties — single-family homes, condos, and HOA properties.",
    canonicalPath: "/miami-services",
    
    directAnswer: (
      <>
        <p>
          We provide impact window and door installation services throughout <strong>Miami-Dade, Broward, and Palm Beach counties</strong> in South Florida. Our service area includes single-family homes, condos, townhomes, and multi-unit buildings. We handle projects ranging from <strong>$15,000 to $100,000+</strong>, from small condo upgrades to full estate replacements.
        </p>
      </>
    ),

    sections: [
      {
        title: "Service Areas",
        testId: "service-areas",
        content: (
          <p className="mb-6">
            We serve the following counties and major cities:
          </p>
        ),
        table: {
          headers: ["County", "Major Cities Served", "Service Notes"],
          rows: [
            [
              "Miami-Dade County",
              "Miami, Coral Gables, Kendall, Doral, North Miami, Aventura, Homestead, Miami Beach, Pinecrest, South Miami, Cutler Bay, Palmetto Bay",
              "Full service area; same-day field measurements available"
            ],
            [
              "Broward County",
              "Fort Lauderdale, Pembroke Pines, Hollywood, Miramar, Plantation, Davie, Sunrise, Coral Springs, Pompano Beach",
              "Full service; 1–2 day scheduling for field visits"
            ],
            [
              "Palm Beach County",
              "Boca Raton, Delray Beach, West Palm Beach, Wellington, Boynton Beach, Jupiter, Lake Worth",
              "Full service; 2–3 day scheduling for field visits"
            ]
          ]
        },
        highlight: "If your city isn't listed, contact us — we may still be able to serve your area depending on project size and location."
      },

      {
        title: "Project Types We Handle",
        testId: "project-types",
        content: (
          <p className="mb-4">
            We specialize in residential impact window and door installations for:
          </p>
        ),
        bullets: [
          <><strong>Single-Family Homes:</strong> Full-house replacements, partial upgrades, hurricane protection retrofits</>,
          <><strong>Condos & Townhomes:</strong> Unit-specific installations with HOA coordination and approval</>,
          <><strong>Waterfront Properties:</strong> Coastal and canal-side homes requiring HVHZ-rated products</>,
          <><strong>Mid-Rise & High-Rise Buildings:</strong> Multi-unit projects with complex access and staging requirements</>,
          <><strong>Historic Homes:</strong> Custom sizing and design-sensitive installations (with city approvals)</>,
          <><strong>New Construction Support:</strong> Builder partnerships for impact window installations in new developments</>
        ]
      },

      {
        title: "What We Offer",
        testId: "what-we-offer",
        content: (
          <p className="mb-4">
            Every project includes:
          </p>
        ),
        bullets: [
          <><strong>Remote Photo-Based Quoting:</strong> Upload photos, receive Good/Better/Premium pricing within 24 hours — no in-home sales visit required</>,
          <><strong>Dedicated Project Manager:</strong> Single point of contact from quote to final inspection</>,
          <><strong>Full Permit Handling:</strong> We prepare, submit, and manage all city permits and HOA approvals</>,
          <><strong>Licensed Installation Crews:</strong> CGC1524851 — fully licensed, insured, and bonded</>,
          <><strong>Code Compliance:</strong> All products are Miami-Dade NOA approved and HVHZ-certified</>,
          <><strong>Warranty & Documentation:</strong> Manufacturer warranties, installation certificates, and wind mitigation documentation provided</>
        ]
      },

      {
        title: "Typical Project Sizes & Pricing",
        testId: "project-sizes",
        table: {
          headers: ["Property Type", "Typical Openings", "Estimated Cost Range", "Timeline"],
          rows: [
            ["Small Condo/Townhome", "6–10 openings", "$12,000 – $18,000", "6–8 weeks"],
            ["Medium Condo/Townhome", "10–15 openings", "$18,000 – $28,000", "7–9 weeks"],
            ["Standard Single-Family Home", "12–18 openings", "$20,000 – $35,000", "8–10 weeks"],
            ["Large Single-Family Home", "18–25 openings", "$30,000 – $50,000", "9–12 weeks"],
            ["Estate / Custom Home", "25+ openings", "$50,000 – $100,000+", "10–14 weeks"]
          ]
        },
        highlight: "Pricing varies based on product tier (Good/Better/Premium), window sizes, and installation complexity. These are typical ranges for standard projects."
      },

      {
        title: "Why Choose Us for Your Miami Project",
        testId: "why-choose-us",
        bullets: [
          <><strong>No Sales Pressure:</strong> Our remote quoting process eliminates the 2–3 hour in-home sales pitch. You get transparent pricing and choose when you're ready.</>,
          <><strong>Local Expertise:</strong> 30+ years of combined experience in South Florida. We understand Miami-Dade permitting, HOA requirements, and HVHZ codes.</>,
          <><strong>Fully Licensed & Insured:</strong> CGC1524851 — we carry full general contractor licensing and comprehensive insurance.</>,
          <><strong>Transparent Process:</strong> You'll know exactly what to expect at every phase, from quoting to final inspection.</>,
          <><strong>Product Options:</strong> We offer Good/Better/Premium tiers so you can choose what fits your budget and goals — no upselling.</>
        ]
      },

      {
        title: "Special Considerations for Condos & HOAs",
        testId: "condos-hoas",
        content: (
          <div className="space-y-4">
            <p>If you live in a condo or HOA-governed community, we handle the entire approval process:</p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--brand-slate-600)]">
              <li>Submission of product specifications, color samples, and installation plans to your board</li>
              <li>Coordination with property management for access and scheduling</li>
              <li>Compliance with building-specific installation requirements (hours, staging, elevator reservations)</li>
              <li>Providing all required insurance certificates and contractor documentation</li>
            </ul>
            <p className="font-semibold text-[var(--brand-slate-900)] mt-4">Timeline Note:</p>
            <p className="text-[var(--brand-slate-600)]">HOA approval can add 2–4 weeks to the overall timeline. We recommend starting the approval process as early as possible.</p>
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "Get Your Quote",
        description: "Upload photos and receive pricing",
        url: "/get-quote?src=miami_services"
      },
      {
        title: "Impact Window Pricing",
        description: "See current pricing for your home",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Installation Process",
        description: "Step-by-step timeline",
        url: "/installation-process"
      },
      {
        title: "See a Real Project",
        description: "Kendall case study",
        url: "/case-study-kendall-full-house"
      }
    ],

    ctaText: "Serve Your Area?",
    ctaSubtext: "Upload photos to see if we can help with your project",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Miami Services" }
    ]
  };

  return <ContentPageTemplate {...pageData} />;
}
